import { getCssVariableValue } from "@gettactic/helpers/src/css/getCssVariableValue";
import React from "react";
import { MapInteractiveZone, SpotShape } from "../../../../types";
import { MapCanvasContext } from "../../contexts/MapCanvasContext/useMapCanvas";
import { SpotLayout } from "../../types";
import { getInteractiveZoneStyles } from "./helpers";

export interface SpotStateAreaSVGProps {
  shape: SpotShape;
  layout: SpotLayout;
  interactiveZone?: MapInteractiveZone;
  /** The spot draws its own rect (desk, room, parking, circle). */
  hasRect: boolean;
  cornerRadius: number;
  isSpotHovered?: boolean;
  /** Draw in the editor too, with the resting (available) state when no
   *  live state is known, so the configured area looks as it will on the
   *  map. */
  isEditorPreview?: boolean;
}

/** A resource with nothing booked: what an area looks like at rest. */
const RESTING_ZONE = {
  zoneId: "",
  notAssigned: false,
  requiresApproval: false,
  disabled: false,
  isOccupied: false,
  reservedSlots: 0,
  hasSchedules: false,
  showCircleProfilePhoto: false,
  showPulse: false
} as MapInteractiveZone;

/**
 * Availability-state highlight area (viewer only): tints a region of the
 * spot — the room rect or a custom rectangle/polygon — with the state
 * color, so e.g. a whole meeting room reads as occupied, not just its dot.
 * Shared by the `ViewSVG` spot and the SVG editor's viewer so both maps
 * paint the same area.
 */
export const SpotStateAreaSVG: React.FC<SpotStateAreaSVGProps> = ({
  shape,
  layout,
  interactiveZone,
  hasRect,
  cornerRadius,
  isSpotHovered = false,
  isEditorPreview = false
}) => {
  // Optional: the editor's bare canvas has no canvas context.
  const isViewMode = React.useContext(MapCanvasContext)?.isViewMode ?? false;
  const stateArea = shape.stateArea;
  if (!stateArea) return null;
  if (!isEditorPreview && (!isViewMode || !interactiveZone)) return null;
  const zone = interactiveZone ?? RESTING_ZONE;

  let stateAreaRect: {
    x: number;
    y: number;
    width: number;
    height: number;
    cornerRadius?: number;
    rotation?: number;
  } | null = null;
  if (stateArea.kind === "spot-rect") {
    if (hasRect && layout.rect.width && layout.rect.height) {
      stateAreaRect = {
        x: layout.rect.x,
        y: layout.rect.y,
        width: layout.rect.width,
        height: layout.rect.height,
        cornerRadius,
        rotation: layout.rect.rotation
      };
    }
  } else if (
    stateArea.kind === "rect" &&
    stateArea.rect?.width &&
    stateArea.rect?.height
  ) {
    stateAreaRect = stateArea.rect;
  }
  // Drawn polygon area (spot-local points).
  const stateAreaPolygon =
    stateArea.kind === "polygon" && (stateArea.points?.length ?? 0) >= 3
      ? (stateArea.points as { x: number; y: number }[])
      : null;
  if (!stateAreaRect && !stateAreaPolygon) return null;

  const areaStyles = getInteractiveZoneStyles({
    isSelected: zone.isSelected || false,
    isLastAccessed: zone.isLastUsed || false,
    isOccupied: zone.isOccupied || false,
    notAssignedResource: zone.notAssigned || false,
    notAssignedUser: !zone.assignedUser,
    requiresApproval: zone.requiresApproval || false,
    isWalkUp: zone.isWalkUp || false,
    hasSchedules: zone.hasSchedules || false,
    showCircleProfilePhoto: zone.showCircleProfilePhoto || false,
    hasCheckedIn: zone.actualSchedule?.has_checked_in || false
  });
  // The dot can legitimately be transparent (reserved, not checked in) but
  // an invisible highlight area defeats its purpose — fall back to the
  // unavailable color for those states. A configured `occupiedColor` wins
  // over the computed color whenever the resource is occupied/booked.
  const stateAreaFill =
    zone.isOccupied && stateArea.occupiedColor
      ? stateArea.occupiedColor
      : areaStyles.fill !== "transparent"
        ? areaStyles.fill
        : getCssVariableValue("--map-spot-not-bookeable-unavailable-fill");
  if (!stateAreaFill) return null;

  // Area-only spots (hidden dot) have no dot pulse — when selected, the
  // area itself pulses as the selection highlight.
  const isAreaSelectionPulse = !!zone.isSelected && stateArea.hideDot === true;
  const fillOpacity = isAreaSelectionPulse ? 0.35 : isSpotHovered ? 0.6 : 0.4;
  const pulse = isAreaSelectionPulse ? (
    <animate
      attributeName="fill-opacity"
      values="0.2;0.4;0.2"
      dur="2s"
      repeatCount="indefinite"
    />
  ) : null;

  if (stateAreaRect) {
    return (
      <rect
        className="spot-state-area"
        x={stateAreaRect.x}
        y={stateAreaRect.y}
        width={stateAreaRect.width}
        height={stateAreaRect.height}
        rx={stateAreaRect.cornerRadius || 0}
        transform={
          stateAreaRect.rotation
            ? `rotate(${stateAreaRect.rotation} ${stateAreaRect.x} ${stateAreaRect.y})`
            : undefined
        }
        fill={stateAreaFill}
        fillOpacity={fillOpacity}
        stroke={stateAreaFill}
        strokeWidth={1.5}
      >
        {pulse}
      </rect>
    );
  }
  return (
    <polygon
      className="spot-state-area"
      points={(stateAreaPolygon ?? [])
        .map((point) => `${point.x},${point.y}`)
        .join(" ")}
      fill={stateAreaFill}
      fillOpacity={fillOpacity}
      stroke={stateAreaFill}
      strokeWidth={1.5}
    >
      {pulse}
    </polygon>
  );
};
